// Pure mapping layer for the Subitems tab. No React, no hooks, no side effects — everything
// here is derivable straight from a scan response and a bag of callbacks the caller already
// holds. This is the highest-leverage file in the plan: everything the tab renders is a
// function of what toSubitemRows produces, so its correctness is the correctness of the tab.
import type {
  SubitemScanParent,
  SubitemScanRow,
  SubitemScanSubitem,
  SubitemState,
} from "./__fixtures__/discrepancyFixtures";
import { BoardHeader, DiscrepancyGridAction, DiscrepancyGridRow, DisplayValue } from "./DiscrepancyGrid/discrepancyGrid.interface";

/**
 * Header row for the boards a synced subitem spans. Unlike Shared Items' toBoardHeaders
 * (sharedColumnsAdapter.ts), the subitem scan already reports boards as `{ boardId, boardName }`
 * pairs the engine resolved server-side, so there is no "still loading" / "Unknown Board"
 * fallback to reconstruct here — just an in-order passthrough.
 */
export const toSubitemBoardHeaders = (parent: SubitemScanParent): BoardHeader[] =>
  parent.boards.map((board) => ({ boardId: board.boardId, boardName: board.boardName }));

export interface SubitemRowActions {
  onResyncColumn: (subitemSyncId: string, columnKey: string) => void;
  onResyncSubitem: (subitemSyncId: string) => void;
  onResyncName: (subitemSyncId: string) => void;
  onCreateMissing: (subitemSyncId: string, targetBoardIds: string[]) => void;
  onUnlink: (subitemSyncId: string) => void;
  /**
   * Connects an `unlinked_subitem`. Hands back the synthetic `unlinked:<id>` sync id like every
   * other action, so the handler can find the subitem (and its `location`) through
   * `findSubitemContext` exactly as the others do — the Monday id itself is read off
   * `location`, never parsed out of the sync id.
   */
  onConnect: (subitemSyncId: string) => void;
}

// missing / orphaned_record / unlinked_subitem are the engine's terminal states: the subitem
// couldn't be compared column-by-column at all, so the engine reports `rows: []` for them
// (nothing to diff) rather than omitting the state. Branching on `state` — not on `rows.length`
// or `columnsTotal` — is deliberate: the fixture's columnsTotal is 0 for these states, but the
// real engine's is non-zero (subitem-classifier.ts:82,98,210), so anything keyed off that count
// would be right against the fixture and wrong against production.
const TERMINAL_STATES = new Set<SubitemState>(["missing", "orphaned_record", "unlinked_subitem"]);
const isTerminalState = (state: SubitemState): boolean => TERMINAL_STATES.has(state);

// Synthetic column key for the single row terminal states get. Keeping the same
// `${subitemSyncId}::${columnKey}` id shape as every other row (rather than a bespoke format)
// means one id-uniqueness rule covers the whole grid instead of two.
const TERMINAL_ROW_COLUMN_KEY = "__subitem__";

/**
 * `SubitemScanRow.valuesByBoard[boardId]` entry, or null, mapped to the grid's DisplayValue.
 * The engine's per-board entry also carries `dropdownLabels` for dropdown columns — Monday
 * assigns dropdown option ids per board, so a board-local id in `value` cannot be reused to
 * write the same label on a different board. DisplayValue's declared shape has no field for
 * this (it predates the subitem write path), so it's carried as an extra property rather than
 * dropped: leaving the return type uninferred (no `: DisplayValue` annotation) lets TypeScript
 * infer the wider shape here and simply widen it on assignment into `Record<string, DisplayValue
 * | null>` below, instead of tripping an excess-property error on a literal return.
 */
const toDisplayValue = (entry: SubitemScanRow["valuesByBoard"][string]) => {
  // null means the scan found no counterpart for this column on that board at all — distinct
  // from a present-but-empty value, and DisplayValue's contract (`exists: false`) is for the
  // "column doesn't exist on this board" case, not this one, so null passes straight through.
  if (!entry) return null;
  return {
    // Passed through RAW and untyped, exactly as it arrived. Phase 2 learned narrowing/
    // collapsing this value is what made every dropdown cell render "Empty" — a renderer
    // downstream may understand shapes this adapter has no business inspecting.
    value: entry.value,
    text: entry.text,
    exists: true,
    ...(entry.dropdownLabels ? { dropdownLabels: entry.dropdownLabels } : {}),
    // Same conditional-spread discipline as dropdownLabels: absent stays absent, so a
    // renderer can tell "this status has no colour" from "this is not a status at all"
    // and fall back deliberately rather than painting an invented default.
    ...(entry.labelColor ? { labelColor: entry.labelColor } : {}),
    // A board_relation has nothing else to render: the engine reports value: "" and
    // text: "" for it, because Monday returns null for both on API 2025-10. Without
    // this the cell is blank on every board, so a drifting row tells the user two
    // boards disagree while showing nothing to compare.
    ...(entry.linkedItemIds ? { linkedItemIds: entry.linkedItemIds } : {}),
  };
};

const toValuesByBoard = (
  valuesByBoard: SubitemScanRow["valuesByBoard"],
): Record<string, DisplayValue | null> =>
  Object.fromEntries(
    Object.entries(valuesByBoard).map(([boardId, entry]) => [boardId, toDisplayValue(entry)]),
  );

/**
 * `{N} of {M} columns drifting` for a group header — computed from the SUBITEM's own
 * columnsAffected/columnsTotal (not derived by re-counting `rows`), so it stays correct however
 * many rows the adapter itself decides to emit for that subitem. Omitted for terminal states:
 * they have no comparable columns, and their columnsTotal is the exact field the fixture gets
 * wrong relative to the engine (see TERMINAL_STATES' comment) — the safest way to never encode
 * that inaccuracy is to never print a sentence built from it.
 */
const driftSummary = (subitem: SubitemScanSubitem): string | undefined => {
  // The one terminal state with something worth a sentence: WHERE the subitem lives, which is
  // the first thing a user asks about a subitem SIMB does not know — and the one thing the
  // payload could not say until the engine started carrying `location` (2026-09-11).
  if (subitem.state === "unlinked_subitem") {
    return subitem.location ? `Lives on ${subitem.location.boardName} — not synced yet` : undefined;
  }
  return isTerminalState(subitem.state)
    ? undefined
    : `${subitem.columnsAffected} of ${subitem.columnsTotal} columns drifting`;
};

/**
 * The subitem-level "Sync all", carried on the GROUP rather than any row. It used to be a
 * stack of buttons above the grid, one per drifting subitem, each repeating that subitem's
 * name in its own label — which duplicated the group headers directly beneath it and grew
 * without bound as a parent gained subitems. In the band it sits on the thing it acts on and
 * needs no name at all.
 *
 * Only for the two comparable drifting states. `ok` has nothing to sync, and the terminal
 * states carry their own create/unlink remedies on their single row.
 */
const bulkResyncActions = (
  subitem: SubitemScanSubitem,
  actions: SubitemRowActions,
): DiscrepancyGridAction[] | undefined =>
  subitem.state === "value_drift" || subitem.state === "name_drift"
    ? [
        {
          key: "resync-subitem",
          label: "Sync all",
          // Every band's button reads "Sync all", so the subitem's name has to come from
          // somewhere for a non-sighted reader — the band gives it visually, this gives it
          // by voice.
          ariaLabel: `Sync all columns on ${subitem.name}`,
          kind: "button",
          onClick: () => actions.onResyncSubitem(subitem.subitemSyncId),
        },
      ]
    : undefined;

const resyncColumnAction = (
  subitemSyncId: string,
  columnKey: string,
  actions: SubitemRowActions,
): DiscrepancyGridAction => ({
  key: "resync-column",
  label: "Sync Latest",
  kind: "button",
  onClick: () => actions.onResyncColumn(subitemSyncId, columnKey),
});

const resyncNameAction = (subitemSyncId: string, actions: SubitemRowActions): DiscrepancyGridAction => ({
  key: "resync-name",
  label: "Sync Latest",
  kind: "button",
  // Decision 2: the __name__ row is not a column row. It always dispatches resync-name, never
  // resync-column, regardless of what a caller might otherwise infer from its row shape.
  onClick: () => actions.onResyncName(subitemSyncId),
});

/** One grid row per SubitemScanRow the engine reported for a comparable (non-terminal) subitem. */
const toColumnRow = (
  subitem: SubitemScanSubitem,
  row: SubitemScanRow,
  actions: SubitemRowActions,
): DiscrepancyGridRow => {
  const isNameRow = row.columnKey === "__name__";

  return {
    // Row id is `${subitemSyncId}::${columnKey}`, never columnKey alone: every subitem in a
    // parent reuses the same column keys (e.g. every one has a "Status::status" row), so the
    // column key alone collides across subitems. A colliding id is a colliding React key, and
    // a colliding key silently drops one of the two rows from the rendered grid.
    id: `${subitem.subitemSyncId}::${row.columnKey}`,
    label: isNameRow ? "Subitem name" : row.columnTitle,
    groupId: subitem.subitemSyncId,
    groupLabel: subitem.name,
    groupSummary: driftSummary(subitem),
    groupState: subitem.state,
    groupActions: bulkResyncActions(subitem, actions),
    valuesByBoard: toValuesByBoard(row.valuesByBoard),
    state: row.state,
    // "ok" rows get no action at all — there is nothing to sync — and the name row always gets
    // the name action regardless of everything else about it (Decision 2).
    actions: isNameRow
      ? [resyncNameAction(subitem.subitemSyncId, actions)]
      : row.state === "ok"
        ? undefined
        : [resyncColumnAction(subitem.subitemSyncId, row.columnKey, actions)],
    // meta is the passthrough the grid's renderValue and the action handlers read, so they
    // never need to parse subitemSyncId/columnKey back out of `id`. labelsByBoard rides along
    // only when the engine emitted it (label-bearing status/dropdown column AND drifting) —
    // Phase 3a's resync-column endpoint 400s a status/dropdown request that omits it, so
    // normalising an absent value to {} here would silently manufacture a payload the real
    // backend rejects.
    meta: {
      subitemSyncId: subitem.subitemSyncId,
      columnKey: row.columnKey,
      columnTitle: row.columnTitle,
      columnType: row.columnType,
      ...(row.labelsByBoard ? { labelsByBoard: row.labelsByBoard } : {}),
    },
  };
};

const terminalActions = (
  subitem: SubitemScanSubitem,
  actions: SubitemRowActions,
): DiscrepancyGridAction[] | undefined => {
  if (subitem.state === "missing") {
    return [
      {
        key: "create-missing",
        label: "Create",
        kind: "button",
        // missingOnBoards is the SOLE source of targetBoardIds — terminal states carry no
        // `rows`, so there is no valuesByBoard to infer absent boards from even if we wanted to.
        onClick: () => actions.onCreateMissing(subitem.subitemSyncId, subitem.missingOnBoards),
      },
    ];
  }
  if (subitem.state === "orphaned_record") {
    return [
      {
        key: "unlink",
        label: "Unlink",
        kind: "button",
        onClick: () => actions.onUnlink(subitem.subitemSyncId),
      },
    ];
  }
  // Phase 3a Decision 3 gave this state no action, on the grounds that adopting the subitem
  // INTO AN EXISTING group would need name-matching. Connect (2026-09-11) mints a NEW group
  // for it and creates its copies — the sync-setup path, scoped to one row — so nothing is
  // matched. Gated on `location`: an engine that predates the field emits the state without
  // it, and a Connect with no Monday id to send would have to parse one out of the synthetic
  // sync id, which is the coupling `location` exists to remove.
  if (subitem.state === "unlinked_subitem" && subitem.location) {
    return [
      {
        key: "connect",
        label: "Connect",
        kind: "button",
        onClick: () => actions.onConnect(subitem.subitemSyncId),
      },
    ];
  }
  return undefined;
};

/**
 * The single row a terminal-state subitem gets. The engine reports no `rows` for these states
 * (nothing was comparable column-by-column), so this is a synthesis, not a mapping: one row,
 * labelled with the subitem's own name, whose per-board values come from `missingOnBoards` —
 * the only board-presence signal a terminal-state subitem carries at all.
 */
const toTerminalRow = (
  subitem: SubitemScanSubitem,
  boardIds: string[],
  actions: SubitemRowActions,
): DiscrepancyGridRow => ({
  id: `${subitem.subitemSyncId}::${TERMINAL_ROW_COLUMN_KEY}`,
  label: subitem.name,
  groupId: subitem.subitemSyncId,
  groupLabel: subitem.name,
  groupSummary: driftSummary(subitem),
  // Terminal states get a band state too, so collapsing one does not hide the fact that it
  // is missing or stale. `bulkResyncActions` returns undefined for all three of them — their
  // remedy is the Create/Unlink button on the single row, not a column-wide sync.
  groupState: subitem.state,
  groupActions: bulkResyncActions(subitem, actions),
  valuesByBoard: Object.fromEntries(
    boardIds.map((boardId) => [
      boardId,
      // `missing` and (since 2026-09-11) `unlinked_subitem` are the terminal states with real
      // per-board evidence: `missingOnBoards` for the first, `location.boardId` for the second
      // — the ONE board the unlinked subitem is on, straight from Monday. For `orphaned_record`
      // the engine deliberately reports missingOnBoards: [] even though it detected the state
      // from `present.length < rows.length` internally — that detection is not carried into
      // the payload (subitem-classifier.ts:76-87). Claiming `exists: true` there would assert a
      // presence we have no evidence for — the same class of error the plan forbids elsewhere
      // ("an unverified scan must never be shown as a clean result"). null ("no counterpart at
      // all", per DisplayValue's own doc comment) is the honest value; the row's state badge,
      // not per-board presence, carries the meaning for that state.
      subitem.state === "missing"
        ? subitem.missingOnBoards.includes(boardId)
          ? null
          : { value: null, text: null, exists: true }
        : subitem.state === "unlinked_subitem" && subitem.location?.boardId === boardId
          ? { value: null, text: null, exists: true }
          : null,
    ]),
  ),
  state: subitem.state,
  actions: terminalActions(subitem, actions),
  meta: { subitemSyncId: subitem.subitemSyncId, columnKey: TERMINAL_ROW_COLUMN_KEY },
});

/**
 * Every grid row for one parent's subitems, grouped by subitemSyncId. `actions.onResyncSubitem`
 * is part of the contract but deliberately unused here — it belongs to a subitem-level bulk
 * action Task 7 wires up outside a single DiscrepancyGridRow, not to any row this adapter emits.
 */
export const toSubitemRows = (parent: SubitemScanParent, actions: SubitemRowActions): DiscrepancyGridRow[] => {
  const boardIds = parent.boards.map((board) => board.boardId);

  return parent.subitems.flatMap((subitem) =>
    isTerminalState(subitem.state)
      ? [toTerminalRow(subitem, boardIds, actions)]
      : subitem.rows.map((row) => toColumnRow(subitem, row, actions)),
  );
};
